import React from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { Loading, Dropdown, HistoryCard } from '@brighterion/br-ui';
import { getCustomUserName, handleKeyEvent, getAdditionalFlagValuesFromStorage } from 'Utils';

import { useAuthenticationStore } from 'Hooks/zustand/useAuthenticationStore';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import useGlobalRules from 'Hooks/query-hooks/global-rules/useGlobalRules';
import useGetLandlordInfo from 'Hooks/query-hooks/tenant/useGetLandlordInfo';
import useRulesHistory from 'Hooks/query-hooks/useRulesHistory';
import useGetReduxUsers from 'Hooks/reduxHelperHooks/useGetReduxUsers';
import useGetParameters from 'Hooks/query-hooks/parameters/useGetParameters';

import LoadMoreButton from 'Components/LoadMoreButton';

import styles from './HistoryCards.css';

/**
 * AccordionItem from br-ui clones its children and passes it the
 * expanded and handleOnClick props
 * so this component was created to cater data fetching for this scenario
 */

type ActualCardsProps = {
    ruleId: string;
    level: HistoryRulesParams['level'];
    subjectIdentifier?: string | number;
    listData?: ListManagementApiList[];
    expanded?: boolean;
    deploymentVersion?: number;
};

const HistoryCards = ({
    ruleId,
    expanded,
    level,
    subjectIdentifier,
    listData,
    deploymentVersion
}: ActualCardsProps) => {
    const { t } = useTranslation();
    const { templateRepositoryId } = useTemplateStore.getState().template;
    const { userInfo } = useAuthenticationStore.getState();
    const { users } = useGetReduxUsers();
    const parametersAPI = useGetParameters({
        queryParams: {
            status: JSON.stringify(['active'])
        }
    });
    const { data: gData } = useGlobalRules({ users, showDeletedRule: true });
    const { data: landlordData } = useGetLandlordInfo(true);

    const conclusionFlagListData: AdditionalConclusionValues[] = getAdditionalFlagValuesFromStorage();

    const globalParameterTypeMap: { [key: string]: string } = {};
    gData?.rules
        .filter((rule) => !rule.nonTemplated)
        .forEach((rule) => {
            rule.inputs.forEach((input) => {
                globalParameterTypeMap[input.fieldName] = input.parameter.name;
            });
        });
    const { fieldNameHashmap } = parametersAPI.data || {};
    let merchantId;
    let tenantGroupId;

    switch (level) {
        case 'MERCHANT':
            merchantId = subjectIdentifier as string;
            break;
        case 'TENANT_GROUP':
            tenantGroupId = Number(subjectIdentifier);
            break;
        default:
            break;
    }
    const rulesHistoryAPI = useRulesHistory({
        queryOptions: {
            enabled: !!expanded
        },
        apiArgs: {
            templateRepositoryId: templateRepositoryId!,
            level,
            ruleId,
            perPage: 30,
            deploymentVersion,
            merchantId,
            tenantGroupId
        },
        listData,
        fieldNameHashmap,
        globalParameterTypeMap,
        isTenantWithLandlord: landlordData?.tenantWithLandlord || false
    });
    if (!expanded) return null;

    if (
        rulesHistoryAPI.infiniteQuery.isFetching ||
        rulesHistoryAPI.infiniteQuery.isLoading ||
        rulesHistoryAPI.countQuery.isLoading ||
        rulesHistoryAPI.countQuery.isFetching
    ) {
        return (
            <div className={styles.loadingContainer}>
                <Loading />
            </div>
        );
    }

    const { custom } = (rulesHistoryAPI.infiniteQuery.data as typeof rulesHistoryAPI.infiniteQuery.data & {
        custom: { [key: string]: HistoryRulesResponse[] };
    }) ?? { custom: {} };
    const dataToMap = _.toPairs(custom);
    const isEmpty = dataToMap.length === 0;

    return (
        <>
            <div>
                {isEmpty && <span>{t('noRevisionHistory')}</span>}
                {!isEmpty &&
                    dataToMap?.map(([key, val], index) => {
                        return (
                            <HistoryCard.CardContainer
                                dataTestId={`container-${index}`}
                                key={_.uniqueId(key)}
                                title={key}
                                rightComponent={
                                    index === 0 && (
                                        <Dropdown id='set-order' menuAlignment='left' arrowSize='sm'>
                                            <div>
                                                {rulesHistoryAPI.order === 'desc'
                                                    ? t('Newest to Oldest')
                                                    : t('Oldest to Newest')}
                                            </div>
                                            <div
                                                tabIndex={0}
                                                role='button'
                                                onKeyDown={handleKeyEvent(() => rulesHistoryAPI.setOrder('desc'))}
                                                onClick={() => rulesHistoryAPI.setOrder('desc')}
                                            >
                                                {t('Newest to Oldest')}
                                            </div>
                                            <div
                                                tabIndex={0}
                                                role='button'
                                                onKeyDown={handleKeyEvent(() => rulesHistoryAPI.setOrder('asc'))}
                                                onClick={() => rulesHistoryAPI.setOrder('asc')}
                                            >
                                                {t('Oldest to Newest')}
                                            </div>
                                        </Dropdown>
                                    )
                                }
                            >
                                {val.map((aVal, cardIndex) => {
                                    conclusionFlagListData.forEach((conclusionVal: AdditionalConclusionValues) => {
                                        const regex = new RegExp(conclusionVal.name, 'g');
                                        aVal._formatted.changeDetails[0] = aVal._formatted.changeDetails[0]?.replace(
                                            regex,
                                            conclusionVal.description
                                        );
                                    });

                                    /**
                                     * After editing the Rule, history revision shows all default values.
                                     * This should not be visible for the user. Instead show: `Rule {RuleId} Added`c.
                                     */
                                    if (aVal._formatted.changeAction === 'Rule Added') {
                                        aVal._formatted.changeDetails = [`Rule ${aVal.ruleId} added`];
                                    }

                                    return (
                                        <HistoryCard.Card
                                            className={styles.historyCard}
                                            dataTestId={`card-${cardIndex}`}
                                            key={`${aVal.createdAt}
                                                ${getCustomUserName(aVal.createdBy, users)} 
                                                ${aVal._formatted.changeAction}
                                                `}
                                            tinyTitle={aVal._formatted.time}
                                            titleSibling={
                                                <>
                                                    <span>{aVal._formatted.changeAction}</span> {t('by')}{' '}
                                                    <span>{getCustomUserName(aVal.createdBy, users)}</span>
                                                    {aVal.alteredByLandlord && (
                                                        <>
                                                            {' '}
                                                            <span className={styles.alteredByLandlord}>
                                                                ({userInfo?.landlordId?.toLowerCase()})
                                                            </span>
                                                        </>
                                                    )}
                                                </>
                                            }
                                            actions={aVal._formatted.changeDetails}
                                        />
                                    );
                                })}
                            </HistoryCard.CardContainer>
                        );
                    })}
                {rulesHistoryAPI.infiniteQuery.isError && (
                    <div className={styles.errorContainer}>{t('Error loading revision data')}</div>
                )}
            </div>
            {rulesHistoryAPI.infiniteQuery.isFetchingNextPage && (
                <div className={styles.buttonContainer}>
                    <Loading size='md' />
                </div>
            )}
            {rulesHistoryAPI.infiniteQuery.hasNextPage && (
                <div className={styles.buttonContainer}>
                    <LoadMoreButton
                        disabled={
                            rulesHistoryAPI.infiniteQuery.isFetchingNextPage || rulesHistoryAPI.countQuery.isLoading
                        }
                        onClick={rulesHistoryAPI.infiniteQuery.fetchNextPage}
                    />
                </div>
            )}
        </>
    );
};

export default React.memo(HistoryCards);
